import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Save, Trash2, FileText, Plus, Edit3, Paperclip, Zap, AlertTriangle, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Provider detail dialog. Shows editable header, visit history, and a
 * "Log visit" form with multi-PDF upload (managers only). PDFs are stored via
 * the /api/upload endpoint (Emergent object storage) and referenced by their
 * storage path in the visit row. Non-managers see visit metadata but not PDFs.
 */
const money = (n) =>
  n == null || Number.isNaN(Number(n))
    ? "—"
    : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const openReport = async (path) => {
  // Files are served via /api/files/{path} which needs the auth header, so
  // fetch it as a blob and open the resulting object URL in a new tab. This
  // is the same technique the SupplierDetailDialog uses for PO PDFs.
  try {
    const { data } = await api.get(`/files/${path}`, { responseType: "blob" });
    const url = URL.createObjectURL(data);
    window.open(url, "_blank", "noopener");
    // Revoke shortly after so the browser has time to render.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch {
    toast.error("Could not open report");
  }
};

const SiteServiceDetailDialog = ({
  open,
  provider,
  onClose,
  onEditProvider,
  onChanged,
}) => {
  const { isManager } = useAuth();
  const [visits, setVisits] = useState([]);
  const [busy, setBusy] = useState(false);
  const [logging, setLogging] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => {
    if (!open || !provider?.id) return;
    setLogging(false);
    setForm({});
    api
      .get(`/site-services/providers/${provider.id}/visits`)
      .then(({ data }) => setVisits(data))
      .catch(() => toast.error("Could not load visit history"));
  }, [open, provider?.id]);

  const setStatus = async (v, status) => {
    let reason;
    if (status === "incomplete") {
      // eslint-disable-next-line no-alert
      reason = window.prompt("Why was this visit incomplete? (optional)", "");
      if (reason == null) return;
    }
    try {
      await api.patch(`/site-services/providers/${provider.id}/visits/${v.id}`, {
        status,
        ...(status === "incomplete" ? { incomplete_reason: reason } : {}),
      });
      const { data } = await api.get(`/site-services/providers/${provider.id}/visits`);
      setVisits(data);
      toast.success(status === "incomplete" ? "Marked incomplete — cost set to $0" : "Marked completed");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update visit");
    }
  };

  const startNewVisit = () => {
    setForm({
      visit_date: new Date().toISOString().slice(0, 10),
      technician_name: "",
      cost: provider?.cost_per_visit ?? "",
      notes: "",
      pdf_paths: [],
      pdf_names: [],
      _id: null,
    });
    setLogging(true);
  };

  // Managers hit the pencil next to a visit row to correct a mistake (e.g.
  // forgot to enter the cost). Same inline form as "Log visit" but with the
  // row's values prefilled and the save handler branches to PATCH.
  const startEditVisit = (v) => {
    setForm({
      visit_date: v.visit_date || "",
      technician_name: v.technician_name || "",
      cost: v.cost ?? "",
      notes: v.notes || "",
      pdf_paths: [...(v.pdf_paths || [])],
      pdf_names: (v.pdf_paths || []).map((p) => p.split("/").pop()),
      _id: v.id,
    });
    setLogging(true);
  };

  const uploadPdf = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/upload", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return { path: data.path, name: file.name };
  };

  const handleFiles = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      const uploaded = [];
      for (const f of Array.from(files)) {
        const r = await uploadPdf(f);
        uploaded.push(r);
      }
      setForm((f) => ({
        ...f,
        pdf_paths: [...(f.pdf_paths || []), ...uploaded.map((u) => u.path)],
        pdf_names: [...(f.pdf_names || []), ...uploaded.map((u) => u.name)],
      }));
      toast.success(`${uploaded.length} PDF${uploaded.length === 1 ? "" : "s"} attached`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const saveVisit = async () => {
    if (!form.visit_date) {
      toast.error("Visit date required");
      return;
    }
    setBusy(true);
    try {
      const body = {
        visit_date: form.visit_date,
        technician_name: form.technician_name || null,
        cost: form.cost === "" ? null : Number(form.cost),
        notes: form.notes || null,
        pdf_paths: form.pdf_paths || [],
      };
      if (form._id) {
        await api.patch(
          `/site-services/providers/${provider.id}/visits/${form._id}`,
          body,
        );
        toast.success("Visit updated");
      } else {
        await api.post(
          `/site-services/providers/${provider.id}/visits`,
          body,
        );
        toast.success("Visit logged");
      }
      setLogging(false);
      const { data } = await api.get(
        `/site-services/providers/${provider.id}/visits`,
      );
      setVisits(data);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save visit");
    } finally {
      setBusy(false);
    }
  };

  const deleteVisit = async (vid) => {
    if (!window.confirm("Delete this visit? PDFs stay in storage but hide from the log."))
      return;
    try {
      await api.delete(`/site-services/providers/${provider.id}/visits/${vid}`);
      setVisits((v) => v.filter((x) => x.id !== vid));
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  if (!open || !provider) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onClose}
      data-testid="site-service-detail-dialog"
    >
      <div
        className="nb-card w-[min(880px,96vw)] max-h-[92vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white px-5 py-4 border-b-2 border-black flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="label-eyebrow">
              {provider.kind} · {provider.interval}
            </div>
            <h3 className="font-display text-2xl">{provider.name}</h3>
            <div className="text-xs text-[#607087] mt-0.5">
              Last visit: <b>{provider.last_visit_date || "never"}</b>
              {provider.next_due_date && (
                <>
                  {" · "}Next due: <b>{provider.next_due_date}</b>
                </>
              )}
              {provider.cost_per_visit != null && (
                <>
                  {" · "}
                  <b>{money(provider.cost_per_visit)}</b> per visit
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isManager && (
              <button
                data-testid="site-service-detail-edit"
                onClick={() => onEditProvider?.(provider)}
                className="nb-btn bg-white px-3 py-2 text-sm flex items-center gap-1"
              >
                <Edit3 size={14} strokeWidth={2.5} /> Edit
              </button>
            )}
            <button
              data-testid="site-service-detail-close"
              onClick={onClose}
              className="nb-btn bg-white p-2"
            >
              <X size={16} strokeWidth={2.5} />
            </button>
          </div>
        </div>

        <div className="p-5 flex-1 overflow-y-auto flex flex-col gap-4">
          {/* Contact strip */}
          {(provider.contact_name || provider.phone || provider.email || provider.website) && (
            <div className="nb-card-sm p-3 bg-[#F4F8FB]">
              <div className="label-eyebrow mb-1">Contact</div>
              <div className="text-sm flex flex-wrap gap-x-4 gap-y-1">
                {provider.contact_name && <span><b>{provider.contact_name}</b></span>}
                {provider.phone && <a href={`tel:${provider.phone}`} className="underline">{provider.phone}</a>}
                {provider.email && <a href={`mailto:${provider.email}`} className="underline">{provider.email}</a>}
                {provider.website && (
                  <a href={provider.website} target="_blank" rel="noopener noreferrer" className="underline">
                    {provider.website}
                  </a>
                )}
              </div>
              {provider.notes && (
                <div className="text-xs text-[#607087] mt-2 whitespace-pre-wrap">{provider.notes}</div>
              )}
            </div>
          )}

          {/* Visits header */}
          <div className="flex items-center justify-between">
            <div className="label-eyebrow">Visit history · {visits.length}</div>
            {isManager && !logging && (
              <button
                data-testid="site-service-log-visit"
                onClick={startNewVisit}
                className="nb-btn nb-btn-dark px-3 py-2 text-sm flex items-center gap-1"
              >
                <Plus size={14} strokeWidth={2.5} /> Log visit
              </button>
            )}
          </div>

          {/* Log-visit form */}
          {logging && (
            <div className="nb-card p-4 bg-[#FFF7DB]" data-testid="site-service-log-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="flex flex-col gap-1">
                  <span className="label-eyebrow">Visit date</span>
                  <input
                    data-testid="site-service-visit-date"
                    type="date"
                    value={form.visit_date || ""}
                    onChange={(e) => setForm((f) => ({ ...f, visit_date: e.target.value }))}
                    className="nb-input px-3 py-2"
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="label-eyebrow">Cost ($)</span>
                  <input
                    data-testid="site-service-visit-cost"
                    type="number"
                    inputMode="decimal"
                    value={form.cost ?? ""}
                    onChange={(e) => setForm((f) => ({ ...f, cost: e.target.value }))}
                    className="nb-input px-3 py-2"
                    placeholder="Optional"
                  />
                </label>
              </div>
              <label className="flex flex-col gap-1 mt-3">
                <span className="label-eyebrow">Technician on site</span>
                <input
                  data-testid="site-service-visit-tech"
                  value={form.technician_name || ""}
                  onChange={(e) => setForm((f) => ({ ...f, technician_name: e.target.value }))}
                  className="nb-input px-3 py-2"
                  placeholder="Who did the visit?"
                />
              </label>
              <label className="flex flex-col gap-1 mt-3">
                <span className="label-eyebrow">Notes</span>
                <textarea
                  data-testid="site-service-visit-notes"
                  value={form.notes || ""}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                  className="nb-input px-3 py-2"
                  rows={2}
                  placeholder="Findings, follow-ups, escalation needed…"
                />
              </label>
              <label className="flex flex-col gap-1 mt-3">
                <span className="label-eyebrow">Attach service report(s)</span>
                <input
                  data-testid="site-service-visit-pdf"
                  type="file"
                  accept="application/pdf,image/*"
                  multiple
                  onChange={(e) => handleFiles(e.target.files)}
                  className="text-xs"
                />
                {(form.pdf_paths || []).length > 0 && (
                  <div className="text-xs text-[#1F6F3E] flex flex-wrap gap-1 mt-1">
                    {form.pdf_paths.map((p, i) => (
                      <span
                        key={p}
                        className="inline-flex items-center gap-1 border-2 border-[#1F6F3E] rounded px-1.5 bg-white"
                      >
                        <Paperclip size={10} strokeWidth={2.5} />
                        {form.pdf_names?.[i] || p.split("/").pop()}
                      </span>
                    ))}
                  </div>
                )}
              </label>
              <div className="mt-4 flex justify-end gap-2">
                <button
                  data-testid="site-service-visit-cancel"
                  onClick={() => setLogging(false)}
                  disabled={busy}
                  className="nb-btn bg-white px-3 py-2 text-sm"
                >
                  Cancel
                </button>
                <button
                  data-testid="site-service-visit-save"
                  disabled={busy}
                  onClick={saveVisit}
                  className="nb-btn nb-btn-dark px-4 py-2 text-sm flex items-center gap-1.5"
                >
                  <Save size={14} strokeWidth={2.5} /> {form._id ? "Save changes" : "Save visit"}
                </button>
              </div>
            </div>
          )}

          {/* Visits list */}
          {visits.length === 0 && !logging && (
            <div className="text-sm text-[#607087] italic p-4 text-center">
              No visits logged yet.
            </div>
          )}
          {visits.map((v) => (
            <div
              key={v.id}
              data-testid={`site-service-visit-${v.id}`}
              className={`nb-card-sm p-3 ${v.status === "incomplete" ? "bg-[#FDE7EA]" : ""}`}
            >
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div>
                  <div className="font-display text-sm flex items-center gap-1.5 flex-wrap">
                    {v.visit_date}{" "}
                    {v.technician_name && (
                      <span className="text-[#607087]">· {v.technician_name}</span>
                    )}
                    {v.auto_logged && (
                      <span
                        data-testid={`site-service-visit-auto-${v.id}`}
                        className="inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded bg-[#0A3A7A] text-white"
                      >
                        <Zap size={9} strokeWidth={2.5} /> AUTO
                      </span>
                    )}
                    {v.status === "incomplete" && (
                      <span
                        data-testid={`site-service-visit-incomplete-${v.id}`}
                        className="inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded bg-[#8A1F1F] text-white"
                      >
                        <AlertTriangle size={9} strokeWidth={2.5} /> INCOMPLETE
                      </span>
                    )}
                  </div>
                  {v.status === "incomplete" && v.incomplete_reason && (
                    <div className="text-xs text-[#8A1F1F] mt-0.5">Reason: {v.incomplete_reason}</div>
                  )}
                  {v.notes && (
                    <div className="text-xs text-[#607087] mt-0.5 whitespace-pre-wrap">
                      {v.notes}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <div className="font-mono-num font-bold text-sm">{money(v.cost)}</div>
                  {isManager && (
                    <>
                      {v.status === "incomplete" ? (
                        <button
                          data-testid={`site-service-visit-mark-complete-${v.id}`}
                          onClick={() => setStatus(v, "completed")}
                          className="nb-btn bg-white px-2 py-1 text-[11px] flex items-center gap-1"
                        >
                          <CheckCircle2 size={12} strokeWidth={2.5} /> Completed
                        </button>
                      ) : (
                        <button
                          data-testid={`site-service-visit-mark-incomplete-${v.id}`}
                          onClick={() => setStatus(v, "incomplete")}
                          className="nb-btn bg-white px-2 py-1 text-[11px] flex items-center gap-1 text-[#8A1F1F]"
                        >
                          <AlertTriangle size={12} strokeWidth={2.5} /> Incomplete
                        </button>
                      )}
                      <button
                        data-testid={`site-service-visit-edit-${v.id}`}
                        onClick={() => startEditVisit(v)}
                        className="nb-btn bg-white p-1.5"
                        title="Edit visit"
                      >
                        <Edit3 size={12} strokeWidth={2.5} />
                      </button>
                      <button
                        data-testid={`site-service-visit-delete-${v.id}`}
                        onClick={() => deleteVisit(v.id)}
                        className="nb-btn bg-white p-1.5"
                        title="Delete visit"
                      >
                        <Trash2 size={12} strokeWidth={2.5} />
                      </button>
                    </>
                  )}
                </div>
              </div>
              {isManager && (v.pdf_paths || []).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {v.pdf_paths.map((p) => (
                    <button
                      key={p}
                      data-testid={`site-service-visit-pdf-${v.id}`}
                      onClick={() => openReport(p)}
                      className="inline-flex items-center gap-1 text-[11px] border-2 border-black rounded px-2 py-0.5 bg-[#DDEEFA] text-[#144F82]"
                    >
                      <FileText size={11} strokeWidth={2.5} /> {p.split("/").pop()}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SiteServiceDetailDialog;
